import dayjs from "dayjs"
import { AFFECTS, APPLIES_ON_PRIVATE } from "../constants/pricingConstants"

/**
 * Conflict detection for day / seasonal price adjustments.
 *
 * Two adjustments covering the same day BOTH apply to a booking on that day and their
 * deltas are summed (see applyPriceAdjustments in Bookings/helpers/calculateBookingTotal),
 * so overlapping adjustments silently stack. These helpers catch that overlap at entry
 * time, where the guide still has the context to fix it.
 *
 * Shapes here are the FORM-side camelCase ones the modals produce — appliesOn /
 * customDays / dateRanges[{ value: [dayjs, dayjs] }] / startDate / endDate — not the
 * snake_case API shapes.
 */

// The applies_on contract, mirroring dayAdjustmentApplies in calculateBookingTotal:
// "weekend" is sat+sun, "weekdays" is mon–fri, "custom" is whichever days were picked.
// Kept as literals (as the booking side does) so this helper doesn't depend on a component.
const DAYS_BY_APPLIES_ON = {
  weekend: ["sat", "sun"],
  weekdays: ["mon", "tue", "wed", "thu", "fri"]
}

const daysCoveredBy = ({ appliesOn, customDays }) =>
  DAYS_BY_APPLIES_ON[appliesOn] ?? (customDays || []).filter(Boolean)

/**
 * Adjustments aimed at disjoint ticket types never touch the same price, so they don't
 * conflict. Anything scoped to "all" tickets overlaps everything — including a
 * selected-ticket adjustment, which it covers.
 */
const ticketTargetsOverlap = (first, second) => {
  if (first.affects !== AFFECTS.SELECTED || second.affects !== AFFECTS.SELECTED) return true
  const targets = new Set(second.selectedTickets || [])
  return (first.selectedTickets || []).some((ticketId) => targets.has(ticketId))
}

// Skip deleted records and the candidate's own row (so editing an adjustment never
// reports a conflict with itself).
const isComparable = (candidate, item) =>
  !item._destroy && !(candidate.id != null && item.id === candidate.id) && ticketTargetsOverlap(candidate, item)

const toMonthDay = (date) => (date.month() + 1) * 100 + date.date()

// A repeat-each-year range ignores the stored year and may wrap the year end
// (Dec 20 – Jan 5), so it is compared as one or two month/day windows.
const toMonthDayWindows = ({ start, end }) => {
  const from = toMonthDay(start)
  const to = toMonthDay(end)
  return from <= to ? [[from, to]] : [[from, 1231], [101, to]]
}

const windowsOverlap = ([fromA, toA], [fromB, toB]) => fromA <= toB && fromB <= toA

const rangesOverlap = (first, second, isYearAgnostic) => {
  if (!isYearAgnostic) {
    return !first.start.isAfter(second.end, "day") && !second.start.isAfter(first.end, "day")
  }
  return toMonthDayWindows(first).some((windowA) => toMonthDayWindows(second).some((windowB) => windowsOverlap(windowA, windowB)))
}

const normalizeRanges = (ranges) =>
  (ranges || [])
    .filter((range) => !range._destroy && range.value?.[0] && range.value?.[1])
    .map((range) => ({ start: dayjs(range.value[0]), end: dayjs(range.value[1]) }))

const rangesCoveredBy = (adjustment, defaultScheduleRanges) => {
  if (adjustment.appliesOn === APPLIES_ON_PRIVATE.DEFAULT_SCHEDULE) return normalizeRanges(defaultScheduleRanges)
  if (adjustment.dateRanges?.length) return normalizeRanges(adjustment.dateRanges)
  // Ticketed seasonal adjustments carry a single flat start/end pair instead.
  if (adjustment.startDate && adjustment.endDate) {
    return [{ start: dayjs(adjustment.startDate), end: dayjs(adjustment.endDate) }]
  }
  return []
}

/**
 * The existing day adjustment whose days overlap `candidate`, or null.
 */
export const findConflictingDayAdjustment = (candidate, existing = []) => {
  const candidateDays = new Set(daysCoveredBy(candidate))
  if (candidateDays.size === 0) return null
  return (
    existing.find(
      (item) => isComparable(candidate, item) && daysCoveredBy(item).some((day) => candidateDays.has(day))
    ) ?? null
  )
}

/**
 * The existing seasonal adjustment whose dates overlap `candidate`, or null.
 *
 * If either side repeats yearly the comparison is year-agnostic, because the repeating
 * one comes round every year and would eventually collide with a fixed range anyway.
 */
export const findConflictingSeasonalAdjustment = (candidate, existing = [], defaultScheduleRanges = []) => {
  const candidateRanges = rangesCoveredBy(candidate, defaultScheduleRanges)
  const candidateRepeats = candidate.isRepeatEachYear ?? true

  return (
    existing.find((item) => {
      if (!isComparable(candidate, item)) return false
      const itemRanges = rangesCoveredBy(item, defaultScheduleRanges)

      // "Holidays" — and Default Schedule before any season is set — resolve to no dates
      // the frontend can compare (the backend owns the holiday calendar), so two
      // adjustments on such an option are duplicates on the strength of the option alone.
      if (candidateRanges.length === 0 || itemRanges.length === 0) {
        return candidateRanges.length === 0 && itemRanges.length === 0 && candidate.appliesOn === item.appliesOn
      }

      const isYearAgnostic = candidateRepeats || (item.isRepeatEachYear ?? true)
      return candidateRanges.some((range) => itemRanges.some((other) => rangesOverlap(range, other, isYearAgnostic)))
    }) ?? null
  )
}

/**
 * Whether one adjustment's own date ranges overlap each other — a data-entry slip rather
 * than a pricing problem (an adjustment applies once however many of its ranges match).
 */
export const hasSelfOverlappingRanges = (dateRanges, isRepeatEachYear = true) => {
  const ranges = normalizeRanges(dateRanges)
  return ranges.some((range, index) => ranges.slice(index + 1).some((other) => rangesOverlap(range, other, isRepeatEachYear)))
}
